import { HomeOutlined, LockOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Divider, Form, Input, Space, Tabs } from "antd";
import type { TabsProps } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import api from "../services/api";
import type { LoginFormValues, RegisterFormValues, User } from "../types";

export default function Login({
  onLoginSuccess,
}: { onLoginSuccess?: (user: User, token: string) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("login"); // 'login' or 'register'
  const [loginForm] = Form.useForm<LoginFormValues>();
  const [registerForm] = Form.useForm<RegisterFormValues>();

  // TOTP 2FA state
  const [totpRequired, setTotpRequired] = useState<boolean>(false);
  const [totpUsername, setTotpUsername] = useState<string>("");
  const [totpCode, setTotpCode] = useState<string>("");

  const handleLogin = async (values: LoginFormValues): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post<{
        token: string;
        user: User;
        totp_required?: boolean;
        message?: string;
      }>("/auth/login", {
        username: values.username,
        password: values.password,
      });

      // Check if TOTP is required
      if (response.totp_required) {
        setTotpRequired(true);
        setTotpUsername(values.username);
        setLoading(false);
        return;
      }

      // Normal login (no TOTP)
      const { token, user } = response;

      // Update auth context
      login(user, token);

      // Call parent callback if provided
      if (onLoginSuccess) {
        onLoginSuccess(user, token);
      }

      // Redirect to home
      navigate("/");
    } catch (err) {
      console.error("Login error:", err);
      setError(err instanceof Error ? err.message : t("login.loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPVerify = async (): Promise<void> => {
    if (!totpCode || totpCode.length !== 6) {
      setError(t("login.totp.invalidCode"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post<{ token: string; user: User }>("/auth/totp/verify", {
        username: totpUsername,
        totp_code: totpCode,
      });

      const { token, user } = response;

      // Update auth context
      login(user, token);

      // Call parent callback if provided
      if (onLoginSuccess) {
        onLoginSuccess(user, token);
      }

      // Redirect to home
      navigate("/");
    } catch (err) {
      console.error("TOTP verification error:", err);
      setError(err instanceof Error ? err.message : t("login.totp.invalidCodeRetry"));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = (): void => {
    setTotpRequired(false);
    setTotpUsername("");
    setTotpCode("");
    setError(null);
    loginForm.resetFields();
  };

  const handleRegister = async (values: RegisterFormValues): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      if (values.password !== values.confirmPassword) {
        setError(t("login.passwordsDoNotMatch"));
        setLoading(false);
        return;
      }

      const response = await api.post<{ data?: { message?: string }; message?: string }>(
        "/auth/register",
        {
          username: values.username,
          email: values.email,
          password: values.password,
        }
      );

      // Registration successful - show success message and reset form
      const data = response.data || response;
      setError(null);
      registerForm.resetFields();

      // Show success alert
      const successMessage = data.message || t("login.registrationSuccess");

      // Display success and switch to login tab
      alert(successMessage);
      setActiveTab("login");
      loginForm.resetFields();
    } catch (err) {
      console.error("Register error:", err);
      const errorObj = err as { response?: { data?: { message?: string } }; message?: string };
      setError(
        errorObj.response?.data?.message || errorObj.message || t("login.registrationFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  // TOTP verification screen
  if (totpRequired) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#f0f2f5",
        }}
      >
        <Card
          title={
            <Space>
              <LockOutlined />
              {t("login.totp.title")}
            </Space>
          }
          style={{ width: 400, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
        >
          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

          <Alert
            message={t("login.totp.loggedInAs", { username: totpUsername })}
            description={t("login.totp.enterCode")}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <Input
                size="large"
                placeholder={t("login.totp.codePlaceholder")}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                style={{ fontSize: "24px", textAlign: "center", letterSpacing: "8px" }}
                autoFocus
                onPressEnter={handleTOTPVerify}
                prefix={<LockOutlined />}
              />
            </div>

            <Button
              type="primary"
              size="large"
              block
              onClick={handleTOTPVerify}
              loading={loading}
              disabled={totpCode.length !== 6}
            >
              {t("login.totp.verifyButton")}
            </Button>

            <Button block onClick={handleBackToLogin}>
              {t("login.totp.backButton")}
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  const tabsItems: TabsProps["items"] = [
    {
      key: "login",
      label: t("login.tabs.login"),
      children: (
        <Form form={loginForm} layout="vertical" onFinish={handleLogin} autoComplete="off">
          <Form.Item
            label={t("login.username")}
            name="username"
            rules={[{ required: true, message: t("login.usernameRequired") }]}
          >
            <Input placeholder={t("login.usernamePlaceholder")} />
          </Form.Item>

          <Form.Item
            label={t("login.password")}
            name="password"
            rules={[{ required: true, message: t("login.passwordRequired") }]}
          >
            <Input.Password placeholder={t("login.passwordPlaceholder")} />
          </Form.Item>

          <Button type="primary" htmlType="submit" block loading={loading}>
            {t("login.loginButton")}
          </Button>
        </Form>
      ),
    },
    {
      key: "register",
      label: t("login.tabs.register"),
      children: (
        <Form form={registerForm} layout="vertical" onFinish={handleRegister} autoComplete="off">
          <Form.Item
            label={t("login.username")}
            name="username"
            rules={[{ required: true, message: t("login.usernameRequired") }]}
          >
            <Input placeholder={t("login.registerUsernamePlaceholder")} />
          </Form.Item>

          <Form.Item
            label={t("login.email")}
            name="email"
            rules={[
              { required: true, message: t("login.emailRequired") },
              { type: "email", message: t("login.emailInvalid") },
            ]}
          >
            <Input placeholder={t("login.emailPlaceholder")} />
          </Form.Item>

          <Form.Item
            label={t("login.password")}
            name="password"
            rules={[{ required: true, message: t("login.passwordRequired") }]}
          >
            <Input.Password placeholder={t("login.registerPasswordPlaceholder")} />
          </Form.Item>

          <Form.Item
            label={t("login.confirmPassword")}
            name="confirmPassword"
            rules={[{ required: true, message: t("login.confirmPasswordRequired") }]}
          >
            <Input.Password placeholder={t("login.confirmPasswordPlaceholder")} />
          </Form.Item>

          <Button type="primary" htmlType="submit" block loading={loading}>
            {t("login.registerButton")}
          </Button>
        </Form>
      ),
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "#f0f2f5",
      }}
    >
      <Card
        title={t("login.cardTitle")}
        style={{ width: 400, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
      >
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabsItems} />

        <Divider style={{ margin: "16px 0" }} />

        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Button block icon={<HomeOutlined />} onClick={() => navigate("/")}>
            {t("login.continueWithoutLogin")}
          </Button>
          <div style={{ textAlign: "center", fontSize: 12, color: "#999" }}>
            {t("login.browseWithoutRegistration")}
          </div>
        </Space>
      </Card>
    </div>
  );
}
